/**
 * Global semaphore that limits concurrent favicon/avatar image loads to MAX_CONCURRENT.
 *
 * Without this, rendering a list of N email threads fires N favicon requests
 * simultaneously. All N backend-to-Google round-trips (~2-4 s each) complete at roughly
 * the same time, flooding the JS event queue with response callbacks and blocking the
 * main thread. By throttling to MAX_CONCURRENT loads we spread the callbacks out.
 */

const MAX_CONCURRENT = 4;

let _active = 0;
const _waiters: Array<() => void> = [];

function _drain() {
  while (_waiters.length > 0 && _active < MAX_CONCURRENT) {
    _active++;
    _waiters.shift()!();
  }
}

/**
 * Acquire a favicon loading slot. Resolves immediately if a slot is free,
 * otherwise queues until one is released. Returns a release function that
 * MUST be called when the image finishes loading or errors (or on unmount).
 */
export function acquireFaviconSlot(): Promise<() => void> {
  return new Promise((resolve) => {
    const acquire = () => {
      _active++;
      let released = false;
      resolve(() => {
        if (!released) {
          released = true;
          _active = Math.max(0, _active - 1);
          _drain();
        }
      });
    };

    if (_active < MAX_CONCURRENT) {
      acquire();
    } else {
      _waiters.push(acquire);
    }
  });
}
